import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useProfileDataLoader from 'hooks/usePatientDataLoader';
import { ProfileData } from 'app/my-account/interfaces/profile.interfaces';
import useUserValidation from 'hooks/useUserValidation';
import useUserVerificationStatus, {
  UserVerificationStatus,
} from 'hooks/useUserVerificationStatus';
import { Persona_Inquiry_Status_Enum } from 'graphql/generated/hasura';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CodexFeatureFlags } from 'utilities/interfaces';
import { Validations } from 'const/validation';

type PatientProfileContextValue = {
  fhirPatientDataLoading: boolean;
  fhirDataLoading: boolean;
  profileData: ProfileData | null;
  fhirUserData: ProfileData | null | undefined;
  setProfileData: React.Dispatch<React.SetStateAction<ProfileData | null>>;
  refetch: () => Promise<void>;
  loading: boolean;
  validationLoading: boolean;
  updatePatientValidation: () => void;
  userValidation: Validations;
};

const PatientProfileContext = createContext<PatientProfileContextValue | null>(
  null,
);

export const PatientProfileProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [userVerificationStatus, setUserVerificationStatus] =
    useState<UserVerificationStatus>({
      status: null,
    });

  const {
    fhirPatientDataLoading,
    profileData,
    fhirUserData,
    setProfileData,
    fhirDataLoading,
    refetch,
  } = useProfileDataLoader();

  //User validation
  const { userValidation, loading: validationLoading } =
    useUserValidation(true);

  const [validation, setValidation] = useState<Validations>({
    isPersonalInfoValid: false,
    isContactInfoValid: false,
    isQuestionnaireValid: false,
    isPersonaVerified: false,
    isSudReport: false,
    isSubstance: false,
    isStateDobValid: false,
    generalValidation: false,
  });

  const { userVerificationValidation } = useUserVerificationStatus();
  const { enablePersonaVerification } = useFlags<CodexFeatureFlags>();

  const updatePatientValidation = useCallback(() => {
    userValidation()
      .then((result) => {
        setValidation(result);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [userValidation]);

  useEffect(() => {
    if (!enablePersonaVerification) return;

    const validateUserStatus = async () => {
      const { userVerificationStatus } = await userVerificationValidation();
      setUserVerificationStatus({
        status: userVerificationStatus,
      });
    };
    validateUserStatus().catch((e) =>
      console.log(
        'An error happened while validating the user verification status',
        e,
      ),
    );
  }, [enablePersonaVerification, userVerificationValidation]);

  useEffect(() => {
    if (userVerificationStatus.status !== Persona_Inquiry_Status_Enum.Completed)
      return;
    setValidation((prevState) => ({
      ...prevState,
      isPersonaVerified: true,
    }));
  }, [enablePersonaVerification, userVerificationStatus.status]);

  useEffect(() => {
    updatePatientValidation();
  }, [updatePatientValidation]);

  const value = useMemo<PatientProfileContextValue>(
    () => ({
      fhirPatientDataLoading,
      fhirDataLoading,
      profileData,
      fhirUserData,
      setProfileData,
      refetch,
      updatePatientValidation,
      validationLoading,
      userValidation: validation,
      loading: Boolean(
        fhirPatientDataLoading ||
          validationLoading ||
          fhirDataLoading ||
          (!profileData && !fhirUserData),
      ),
    }),
    [
      fhirPatientDataLoading,
      fhirDataLoading,
      profileData,
      fhirUserData,
      setProfileData,
      refetch,
      updatePatientValidation,
      validation,
      validationLoading,
    ],
  );

  return (
    <PatientProfileContext.Provider value={value}>
      {children}
    </PatientProfileContext.Provider>
  );
};

export const usePatientProfileContext = (): PatientProfileContextValue => {
  const ctx = useContext(PatientProfileContext);
  if (!ctx) {
    throw new Error(
      'usePatientProfileContext must be used within PatientProfileProvider',
    );
  }
  return ctx;
};
